import { z } from "zod";
/**
 * What the Overview is allowed to say while captures are still running.
 *
 * This boundary exists because the screen could not tell two different things
 * apart. "We measured and found nothing" and "we have not measured yet" both
 * rendered as *No captures in this range yet*, so a report whose captures were
 * queued and running showed a person a result — and the result was a null one.
 *
 * The database already draws this line one layer down: `runs.terminal_state` is
 * nullable precisely so a queued capture is not mistaken for an engine that
 * declined to answer. This is the same distinction, carried up to the screen.
 *
 * Every number here is counted from rows. There is no estimate, no smoothing,
 * and nothing that advances on a timer — a progress indicator that moved
 * without a capture behind it would be a placeholder value on a non-test path
 * (AGENTS.md §1), which is the one thing a product built on evidence cannot
 * ship.
 */
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/u, "expected an ISO date, e.g. 2026-08-01");
/** One capture, as the waiting screen lists it. */
export const captureEventSchema = z.object({
    runId: z.string(),
    engine: z.string(),
    promptText: z.string(),
    /**
     * How the capture ended, or null while it is still going.
     *
     * The states are not collapsed into ok/failed here. A login wall and a
     * selector fault are different problems with different fixes, and a screen
     * that shows one word for both hides which one we have.
     */
    terminalState: z.string().nullable(),
    /**
     * Whether the answer named the report's subject brand.
     *
     * Three states, and the third is the point. `null` means we cannot say:
     * either the answer is stored and nobody has read it yet — mentions are
     * written by a separate job that runs after the capture is recorded — or
     * there was no answer to read, because the capture ended at a login wall or
     * a challenge. Reporting `false` in either case would assert the brand was
     * absent from text we never looked at, which is the error this whole
     * boundary exists to stop. On a fast queue it is also the state most rows
     * are in when the screen first paints.
     */
    namedSubject: z.boolean().nullable(),
    /**
     * How long the capture itself took, in milliseconds.
     *
     * Measured from `capture_started_at`, never from `started_at` — the latter is
     * when the run was enqueued, and subtracting it reports queue wait and
     * capture time fused together. Null when the worker did not record a start,
     * which is every run captured before that column existed.
     */
    durationMs: z.number().int().min(0).nullable(),
    finishedAt: z.string().nullable(),
});
/** One outcome that produced no answer, and how many times it happened. */
export const unansweredCountSchema = z.object({
    state: z.string(),
    count: z.number().int().min(0),
});
export const captureProgressSchema = z.object({
    /**
     * The day being reported on: the most recent day this report has any run for.
     *
     * Read from the rows rather than taken as "today", because a report whose
     * captures were planned just before midnight in its own market would
     * otherwise be asked about a day it has nothing on, and answer "nothing is
     * happening" while a worker was mid-capture.
     */
    runDate: isoDate.nullable(),
    /** Runs planned for this report on `runDate`. Zero means nothing was planned. */
    planned: z.number().int().min(0),
    /** Runs with no outcome yet — queued, leased, or in flight. */
    pending: z.number().int().min(0),
    /** Runs that finished with an answer. */
    answered: z.number().int().min(0),
    /** Runs that finished without one, kept split by outcome rather than summed. */
    unanswered: z.array(unansweredCountSchema),
    /**
     * Whether this report had an answer before the batch still running began.
     *
     * This is what decides whether the waiting screen shows at all. A first batch
     * has nothing behind it, so waiting is the whole page. Every later batch —
     * the next morning, or prompts added an hour after onboarding — has a working
     * dashboard behind it, and the dashboard is what the page shows.
     */
    hasPriorAnswers: z.boolean(),
    /** Most recently finished captures, newest first. */
    recent: z.array(captureEventSchema),
    /**
     * Why the pending captures are not moving, when the server knows.
     *
     * `budget`: this month's DataForSEO budget is spent, so every queued capture
     * waits until the month turns or the budget is raised. Without this the rail
     * showed "10/16" frozen for half an hour and nothing said why (dev,
     * 2026-10-05). Absent when nothing is held — never a guess. The Rust API
     * sends it; the Node API never did, which reads as "not held".
     */
    heldFor: z.literal("budget").optional(),
});
//# sourceMappingURL=capture-progress.js.map